import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import ExploreIcon from '@mui/icons-material/Explore';
import WidgetsIcon from '@mui/icons-material/Widgets';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { ROUTES, postLoginDestination } from '../../app/routes';
import type { AuthDestination, DestinationOrganization } from '../../core/api/types';
import './auth.css';

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string;

/** Tile colour + icon per destination code (wireframe hub-02-destinations). */
const LOOK: Record<string, { icon: typeof SettingsIcon; bg: string; fg: string }> = {
  ARCHITECH: { icon: ExploreIcon, bg: '#e5f6ec', fg: '#21804a' },
  PLATFORM: { icon: SettingsIcon, bg: '#eef0f3', fg: '#4b5563' },
  RESTAURANT: { icon: RestaurantIcon, bg: '#e3ecff', fg: '#2f5fb8' },
  COSMETICS: { icon: SpaIcon, bg: '#fde3ec', fg: '#b03a62' },
  HOTEL: { icon: HotelIcon, bg: '#dff3e6', fg: '#21804a' },
  PHARMACY: { icon: LocalPharmacyIcon, bg: '#e7f5f7', fg: '#0f7c8a' },
  AUTOSERVICE: { icon: CarRepairIcon, bg: '#fff1d6', fg: '#9a6400' },
  HUB: { icon: ExploreIcon, bg: '#e5f6ec', fg: '#21804a' },
  REALESTATE: { icon: HomeWorkIcon, bg: '#efe7fd', fg: '#6d28d9' },
  SUPERMARKET: { icon: LocalGroceryStoreIcon, bg: '#ecf6dc', fg: '#4d7c0f' },
  CONSTRUCTION: { icon: ConstructionIcon, bg: '#fdf0dc', fg: '#b45309' },
  FINANCE: { icon: AccountBalanceIcon, bg: '#dff4f1', fg: '#0f766e' },
};

export function destinationTitle(t: Translate, destination: AuthDestination): string {
  if (destination.code === 'ARCHITECH') return 'Biznesi';
  if (destination.code === 'PLATFORM') return t('destinations.accountConfiguration', 'My account & Configuration');
  if (destination.code === 'HUB') return t('destinations.account', 'My account');
  return destination.displayName;
}

function destinationHint(t: Translate, code: string): string {
  switch (code) {
    case 'ARCHITECH': return t('destinations.hint.ARCHITECH', 'Discover products, services and businesses');
    case 'PLATFORM': return t('destinations.hint.accountConfiguration', 'users, roles, company profile, jobs, reservations, orders, applications');
    case 'RESTAURANT': return t('destinations.hint.RESTAURANT', 'tables, orders, kitchen');
    case 'COSMETICS': return t('destinations.hint.COSMETICS', 'calendar, POS, shop');
    case 'HOTEL': return t('destinations.hint.HOTEL', 'rooms, reservations, guests');
    case 'PHARMACY': return t('destinations.hint.PHARMACY', 'stock, sales, prescriptions');
    case 'AUTOSERVICE': return t('destinations.hint.AUTOSERVICE', 'work orders, parts, customers');
    case 'HUB': return t('destinations.hint.HUB', 'reservations, orders, applications');
    case 'REALESTATE': return t('destinations.hint.REALESTATE', 'listings, viewings, contracts');
    case 'SUPERMARKET': return t('destinations.hint.SUPERMARKET', 'checkout, stock, suppliers');
    case 'CONSTRUCTION': return t('destinations.hint.CONSTRUCTION', 'projects, sites, materials');
    case 'FINANCE': return t('destinations.hint.FINANCE', 'accounts, invoices, reports');
    default: return '';
  }
}

/**
 * Where a destination opens (Hub 02 §2 rule 6). PLATFORM honours an interrupted protected deep
 * link, HUB opens the personal area, a product goes through the one-time launch at `/launch/:code`.
 */
export function destinationRoute(destination: AuthDestination, from?: string | null): string {
  if (destination.code === 'ARCHITECH') return ROUTES.landing;
  if (destination.code === 'PLATFORM') return postLoginDestination(from);
  // HUB-UI: preserve personal deep links, including a vacancy awaiting sign-in.
  if (destination.code === 'HUB') return from && /^\/me(?:\/|\?|$)/.test(from) ? from : ROUTES.personalAccount;
  return ROUTES.launchProduct.replace(':productCode', destination.code.toLowerCase());
}

export interface DestinationChoice {
  organizationId: string;
  destination: AuthDestination;
}

export function countDestinations(organizations: DestinationOrganization[]): number {
  return organizations.reduce((sum, organization) => sum + organization.destinations.length, 0);
}

export function firstDestination(organizations: DestinationOrganization[]): DestinationChoice | null {
  const organization = organizations.find((item) => item.destinations.length > 0);
  return organization ? { organizationId: organization.id, destination: organization.destinations[0] } : null;
}

// ---------------------------------------------------------------------------------------------
// "Always open this" — remembered on this device until identity.UserPreferences is served
// (BE-07, PUT /me/preferences). Keyed by the normalized login; holds ids only, never credentials.
// ---------------------------------------------------------------------------------------------

const DEFAULT_KEY = 'architech.platform.v1.defaultDestination';

function readDefaults(): Record<string, { organizationId: string; code: string }> {
  try { return JSON.parse(localStorage.getItem(DEFAULT_KEY) ?? '{}') ?? {}; } catch { return {}; }
}

export const savedDestination = {
  get(login: string): { organizationId: string; code: string } | null {
    return readDefaults()[login.trim().toLowerCase()] ?? null;
  },
  set(login: string, choice: { organizationId: string; code: string }) {
    try { localStorage.setItem(DEFAULT_KEY, JSON.stringify({ ...readDefaults(), [login.trim().toLowerCase()]: choice })); } catch { /* storage blocked */ }
  },
  clear(login: string) {
    const all = readDefaults();
    delete all[login.trim().toLowerCase()];
    try { localStorage.setItem(DEFAULT_KEY, JSON.stringify(all)); } catch { /* storage blocked */ }
  },
};

/** Resolves a saved (or server) default only while it is still offered. */
export function resolveDefault(organizations: DestinationOrganization[], saved: { organizationId: string; code: string } | null | undefined): DestinationChoice | null {
  if (!saved) return null;
  const organization = organizations.find((item) => item.id === saved.organizationId);
  const destination = saved.code === 'ARCHITECH' && organization ? architechDestination : organization?.destinations.find((item) => item.code === saved.code);
  return organization && destination ? { organizationId: organization.id, destination } : null;
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase()).join('');
}

const architechDestination: AuthDestination = { code: 'ARCHITECH', kind: 'account', displayName: 'Biznesi', icon: 'Explore' };

interface PickerProps {
  /** An enclosing modal supplies the dialog role and focus management. */
  embedded?: boolean;
  organizations: DestinationOrganization[];
  firstName?: string | null;
  busy?: boolean;
  error?: string | null;
  /** Shows the "Always open this" checkbox (sign-in only). */
  allowRemember?: boolean;
  onOpen: (choice: DestinationChoice, remember: boolean) => void;
  onClose?: () => void;
  closeLabel?: string;
}

interface PickerSection {
  key: string;
  organization?: DestinationOrganization;
  choices: DestinationChoice[];
}

/**
 * Display order: one section per organization with its product consoles, then "My account & marketplace"
 * (Biznesi and the combined account/configuration entry). Every card keeps the
 * organization it belongs to, so opening it still switches to the right tenant.
 */
function pickerSections(organizations: DestinationOrganization[]): PickerSection[] {
  const visible = organizations.filter((organization) => organization.destinations.length > 0);
  const isAccountOrg = (organization: DestinationOrganization) => organization.destinations.every((destination) => destination.code === 'HUB');
  const configurationOrg = visible.find((organization) => !isAccountOrg(organization) && organization.destinations.some((destination) => destination.code === 'PLATFORM'));
  const configuration = configurationOrg?.destinations.find((destination) => destination.code === 'PLATFORM');
  const account: DestinationChoice[] = [];
  if (visible[0]) account.push({ organizationId: (configurationOrg ?? visible[0]).id, destination: architechDestination });
  if (configurationOrg && configuration) account.push({ organizationId: configurationOrg.id, destination: configuration });
  for (const organization of configuration ? [] : visible.filter(isAccountOrg)) {
    for (const destination of organization.destinations) account.push({ organizationId: organization.id, destination });
  }
  const sections: PickerSection[] = [];
  for (const organization of visible) {
    if (isAccountOrg(organization)) continue;
    const choices = organization.destinations
      .filter((destination) => destination !== configuration)
      .map((destination) => ({ organizationId: organization.id, destination }));
    if (choices.length) sections.push({ key: organization.id, organization, choices });
  }
  if (account.length) sections.push({ key: 'account', choices: account });
  return sections;
}

/** Organizations × destinations, with product consoles first (see pickerSections). */
export function DestinationPicker({ organizations, firstName, busy = false, error, allowRemember = false, onOpen, onClose, closeLabel, embedded = false }: PickerProps) {
  const { t } = useTranslation();
  const sections = useMemo(() => pickerSections(organizations), [organizations]);
  const [selected, setSelected] = useState<DestinationChoice | null>(() => sections[0]?.choices[0] ?? null);
  const [remember, setRemember] = useState(false);
  const isSelected = (choice: DestinationChoice) => selected?.organizationId === choice.organizationId && selected.destination.code === choice.destination.code;
  const selectedOrganization = sections.find((section) => section.choices.some(isSelected))?.organization;
  const selectedTitle = selected ? destinationTitle(t, selected.destination) : '';

  return <div className="ax-dialog" role={embedded ? undefined : 'dialog'} aria-modal={embedded ? undefined : true} aria-labelledby={embedded ? undefined : 'ax-dest-title'}>
    {onClose && <button type="button" className="ax-ghost ax-close" onClick={onClose}>{closeLabel ?? t('destinations.close', 'Close')}</button>}
    <h1 id="ax-dest-title">{firstName
      ? t('destinations.welcomeName', 'Welcome back, {{name}}', { name: firstName })
      : t('destinations.welcome', 'Welcome back')}</h1>
    <p className="lead">{t('destinations.lead', 'Where do you want to go?')}</p>
    {error && <div className="ax-alert error" role="alert">{error}</div>}

    <div className="ax-orgs" role="radiogroup" aria-labelledby="ax-dest-title">
      {sections.map(({ key, organization, choices }) => {
        const title = organization ? organization.displayName : t('destinations.accountGroup', 'My account & marketplace');
        return <section key={key} aria-label={title}>
          <div className="ax-org-head">
            <span className={`ax-avatar${organization ? '' : ' account'}`} aria-hidden
              style={organization?.logoUrl ? { backgroundImage: `url(${organization.logoUrl})` } : undefined}>
              {!organization ? <PersonIcon sx={{ fontSize: 20 }} /> : !organization.logoUrl && initials(organization.displayName)}
            </span>
            <div>
              <b>{title}</b>
              {organization && <small> · {organization.code}</small>}
            </div>
          </div>
          <div className="ax-cards">
            {choices.map((choice) => {
              const { destination } = choice;
              const look = LOOK[destination.code] ?? { icon: WidgetsIcon, bg: '#eef0f3', fg: '#4b5563' };
              const Icon = look.icon;
              return <button key={`${choice.organizationId}:${destination.code}`} type="button" role="radio" aria-checked={isSelected(choice)} className="ax-card" disabled={busy}
                onClick={() => setSelected(choice)}
                onDoubleClick={() => onOpen(choice, remember)}>
                <span className="tile" style={{ background: look.bg, color: look.fg }}><Icon sx={{ fontSize: 28 }} aria-hidden /></span>
                <b>{destinationTitle(t, destination)}</b>
                <small>{destinationHint(t, destination.code)}</small>
              </button>;
            })}
          </div>
        </section>;
      })}
    </div>

    <div className="ax-dialog-foot">
      {allowRemember && selected ? <label className="ax-check">
        <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
        <span>{t('destinations.remember', 'Always open {{destination}} — {{organization}} after sign-in', {
          destination: selectedTitle,
          organization: selectedOrganization ? selectedOrganization.displayName : t('destinations.accountGroup', 'My account & marketplace'),
        })}</span>
      </label> : <span />}
      <button type="button" className="ax-primary" disabled={!selected || busy} onClick={() => selected && onOpen(selected, remember)}>
        {busy ? t('destinations.opening', 'Opening…') : t('destinations.open', 'Open {{destination}}', { destination: selectedTitle })}
        {!busy && <ArrowForwardIcon sx={{ fontSize: 18 }} aria-hidden />}
      </button>
    </div>
  </div>;
}
